import React from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { Modal, ModalHeader, ModalBody, ModalFooter, Row, Col } from "reactstrap";
import { Formik, Field } from "formik";
import { toast } from "react-toastify";
import * as Yup from "yup";

import * as variables from "constants/variables";
import { FormElements, Preloader } from "components";

import * as routines from "../routines";

const TRANSLATION_BASE = "modules.subscription";

export default ({ isOpen = false, data = {}, onClose = () => {} }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const isNew = !Object.values(data).length;

  let title = "";

  if (isNew) {
    title = t(`${TRANSLATION_BASE}.new_subscription`);
  } else {
    title = t(`${TRANSLATION_BASE}.update_subscription`);
  }

  return (
    <Modal isOpen={isOpen}>
      <Formik
        enableReinitialize
        initialValues={{
          plan: data.plan,
          price: data.price,
          period: data.period,
          discount: data.discount || 0,
          description: data.description
        }}
        validationSchema={() =>
          Yup.object().shape({
            plan: Yup.string().required(t("global.validation.field_is_required")),
            price: Yup.number().required(t("global.validation.field_is_required")),
            period: Yup.number().required(t("global.validation.field_is_required")),
            discount: Yup.number()
              .required(t("global.validation.field_is_required"))
              .max(100),
            description: Yup.string()
              .required(t("global.validation.field_is_required"))
              .max(4000, t("global.validation.too_long"))
          })
        }
        onSubmit={(values, { setSubmitting }) => {
          const options = {
            values,
            onSuccess: () => {
              setSubmitting(false);
              toast.success(t("global.notification.success"), {
                position: toast.POSITION.TOP_RIGHT
              });
              onClose();
            },
            onError: (error = {}) => {
              setSubmitting(false);
              toast.error(t("global.error_occurred"), {
                position: toast.POSITION.TOP_RIGHT
              });
            }
          };

          dispatch(
            isNew
              ? routines.create(options)
              : routines.update({
                  ...options,
                  subscription_id: data.id
                })
          );
        }}>
        {({ isSubmitting, handleSubmit }) => {
          return (
            <Preloader loading={isSubmitting}>
              <form onSubmit={handleSubmit}>
                <ModalHeader toggle={onClose}>{title}</ModalHeader>
                <ModalBody className="p-4">
                  <Row>
                    <Col md={12}>
                      <Field
                        name="plan"
                        label={t(`${TRANSLATION_BASE}.plan`)}
                        placeholder={t(`${TRANSLATION_BASE}.plan`)}
                        component={FormElements.Input}
                      />
                    </Col>
                    <Col md={12}>
                      <Field
                        name="price"
                        label={t(`${TRANSLATION_BASE}.price`) + ` (${variables.CURRENCY})`}
                        placeholder={t(`${TRANSLATION_BASE}.price`)}
                        component={FormElements.PriceInput}
                      />
                    </Col>
                    <Col md={12}>
                      <Field
                        name="period"
                        label={t(`${TRANSLATION_BASE}.period_in_months`)}
                        placeholder={t(`${TRANSLATION_BASE}.period`)}
                        component={FormElements.Input}
                      />
                    </Col>
                    <Col md={12}>
                      <Field
                        type="number"
                        name="discount"
                        label={t(`${TRANSLATION_BASE}.discount`) + " (%)"}
                        placeholder={t(`${TRANSLATION_BASE}.discount`)}
                        component={FormElements.Input}
                      />
                    </Col>
                    <Col md={12}>
                      <Field
                        htmlType="textarea"
                        name="description"
                        label={t(`${TRANSLATION_BASE}.description`)}
                        placeholder={t(`${TRANSLATION_BASE}.description`)}
                        component={FormElements.Input}
                      />
                    </Col>
                  </Row>
                </ModalBody>
                <ModalFooter>
                  <button
                    type="button"
                    className="btn btn-secondary waves-effect"
                    data-dismiss="modal"
                    onClick={() => onClose()}>
                    {t("global.close")}
                  </button>
                  <button type="submit" className="btn btn-info waves-effect waves-light">
                    {t("global.save_changes")}
                  </button>
                </ModalFooter>
              </form>
            </Preloader>
          );
        }}
      </Formik>
    </Modal>
  );
};
